import React, { FC } from "react";
import { Fade } from "react-awesome-reveal";
import classNames from "classnames";
import { BlockBody } from "@app/components/common/block-body/BlockBody";
import { BlockHeader } from "@app/components/common/block-header/BlockHeader";
import { IRichText, TextType } from "@app/models/storyblok/basic";
import { IInnerText } from "@app/models/storyblok/blocks/inner-text";
import { DEFAULT_FADE_DURATION } from "@app/constants";
import { checkIsRichTextNotEmpty } from "@app/helpers";
import { COLUMNS_NUMBER_TO_CHANGE_LAYOUT } from "./constants";

type Props = {
  text: IRichText<TextType>;
  subtext: IRichText<TextType>;
  footer?: IRichText<TextType>;
  columns: IInnerText[];
};

export const ColumnsText: FC<Props> = ({ text, subtext, columns, footer }) => {
  const isFooter = checkIsRichTextNotEmpty(footer);

  return (
    <div
      className={classNames(
        "mt-20 px-5",
        "lg:mt-25 lg:px-16",
        "xl:mx-auto xl:mt-40 xl:w-full xl:max-w-container-wide xl:px-24",
      )}
    >
      <Fade duration={DEFAULT_FADE_DURATION} triggerOnce>
        <div
          className={classNames(
            "text-regular text-primary-content",
            "lg:w-4/6 lg:text-medium",
            "xxl:text-large",
          )}
        >
          <BlockBody richText={text} />
        </div>
        {subtext && (
          <div
            className={classNames(
              "mt-3 text-regular text-base-400",
              "lg:mt-8 lg:w-1/2",
              "xxl:mt-14 xxl:w-1/2 xxl:text-medium",
            )}
          >
            <BlockBody richText={subtext} />
          </div>
        )}
        <div
          className={classNames(
            "mt-10 grid grid-cols-1 gap-10",
            "md:grid-cols-3",
            "lg:mt-12 lg:gap-y-15",
            "xxl:mt-20 xxl:gap-y-20",
            {
              "lg:grid-cols-4":
                columns.length > COLUMNS_NUMBER_TO_CHANGE_LAYOUT,
            },
          )}
        >
          {columns.map((column) => (
            <div
              key={column._uid}
              className={classNames(
                "w-full",
                "md:min-w-2/5 md:w-auto",
                "lg:min-w-52",
                "xxl:min-w-80",
              )}
            >
              <BlockHeader
                className={classNames(
                  "mb-2.5 text-regular text-primary-content",
                  "xxl:mb-4.5 xxl:text-medium",
                )}
                richText={column.title}
              />
              <div
                className={classNames(
                  "text-regular text-primary-content [&_ul]:space-y-2.5",
                  "[&_li]:before:bg-storyblok-purple",
                  "[&_li]:relative [&_li]:pl-7",
                  "[&_li]:before:absolute [&_li]:before:left-1.5 [&_li]:before:top-2 [&_li]:before:h-2 [&_li]:before:w-2",
                  "[&_li]:before:rounded-full",
                  "md:[&_li]:pl-5 md:[&_li]:before:left-0",
                  "lg:text-extra-small lg:[&_ul]:space-y-1",
                  "lg:[&_li]:before:top-1.5",
                  "xxl:[&_li]:pl-6 xxl:[&_li]:before:h-3 xxl:[&_li]:before:w-3 xxl:[&_ul]:space-y-2.5",
                  "xxl:text-regular",
                )}
              >
                <BlockBody richText={column.text} />
              </div>
            </div>
          ))}
        </div>
        {isFooter && (
          <div
            className={classNames(
              "mt-12 text-extra-small text-base-400",
              "lg:mt-9",
              "xxl:mt-14 xxl:text-regular",
            )}
          >
            <BlockBody richText={footer} />
          </div>
        )}
      </Fade>
    </div>
  );
};
